import { Link } from '@tanstack/react-router';
import { ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { NavArea, NavItem } from '../../navigation.ts';
import { useSidebar } from '../../lib/layout.ts';
import { useRecent } from '../../lib/recent.ts';
import { useLayer } from '../../lib/modal-focus.ts';
import { useSession } from '../../lib/session.tsx';
import { attemptLeave } from '../../lib/unsaved.tsx';
import { cn } from '../../lib/utils.ts';
import { ProfileMenu } from '../profile-menu.tsx';
import { Tooltip } from '../ui/tooltip.tsx';
import { areaActive, Avatar, CounterBadge, LogoMark, navName, pathMatches, useCounterOf, usePathname, useRoleNav, Wordmark } from './nav-parts.tsx';

/**
 * Seitenleiste Design S3 (Change 11.10.2026, Rudolf Entscheidung S4; DESIGN „Design S3 › Navigation“, EXPERIENCE §2.1,
 * §10; Story 1.77). Schmal: schwebende weiße Symbolleiste 84 px, runde Symbolknöpfe 44 px mit Beschriftung 11 px;
 * Bereiche mit mehreren Einträgen öffnen beim Überfahren oder Fokus ein Ausklapp-Menü daneben (Pfeiltasten wählen,
 * Esc schließt). Breit (220 px): Symbole + Namen + Zähler, Bereiche aufklappbar. Desktop: Zustand aus dem Profil
 * (`sidebar`), „Leiste aufklappen/einklappen“ speichert dort. Tablet: immer schmal; der Knopf legt die breite Leiste als
 * Überlagerung über den Inhalt (Tipp daneben oder Esc schließt), ohne den Profilwert zu ändern.
 */
export function Sidebar({ tablet }: { tablet: boolean }) {
  const { sidebar, toggle } = useSidebar();
  const [overlay, setOverlay] = useState(false);
  const navId = useId();
  const expanded = tablet ? overlay : sidebar === 'expanded';
  const { foot } = useRoleNav();
  const { me } = useSession();
  // „Neuer Auftrag“ und „Suchen“ stehen nur im Kopf („+ Neu“, Suchfeld), nicht doppelt in der Leiste ([RUDOLF] 02.11.2026).

  // Breite Leiste am Tablet ist eine Überlagerung: eigene Ebene, Esc schließt nur sie (wenn sie oben liegt).
  const asideRef = useRef<HTMLElement>(null);
  useLayer(tablet && overlay, { root: asideRef, onEscape: () => setOverlay(false) });
  const closeOverlay = tablet ? () => setOverlay(false) : undefined;
  return (
    <>
      {tablet && overlay ? (
        <div data-testid="seitenleiste-abdunklung" aria-hidden className="fixed inset-0 z-20 bg-scrim" onClick={() => setOverlay(false)} />
      ) : null}
      <aside
        ref={asideRef}
        aria-label="Seitenleiste"
        data-state={expanded ? 'expanded' : 'collapsed'}
        data-overlay={tablet && overlay ? 'true' : undefined}
        className={cn(
          // Redesign 31.10.2026 (Variante C): bündig über die volle Höhe, ruhige graue Fläche ohne Schatten.
          'fixed bottom-0 left-0 flex flex-col bg-surface-side pt-3 pb-3 transition-[width] duration-200 ease-[var(--motion-ease)] motion-reduce:transition-none',
          expanded ? 'w-sidebar-expanded px-[10px]' : 'w-rail items-center px-0',
          // Offenes Ausklapp- oder Profil-Menü liegt über dem Inhalt samt Such-Liste (z-40), aber unter Dialogen und Schubladen (z-50).
          tablet && overlay ? 'z-30 shadow-flyout' : 'z-10 has-[[data-raise]]:z-[45] has-[[role=dialog]]:z-[45]',
        )}
        style={{ top: 'var(--banner-h)' }}
      >
        <div className={cn('flex flex-none items-center pb-3', expanded ? 'gap-[10px] pl-[6px]' : 'flex-col gap-2')}>
          <LogoMark />
          {expanded ? <Wordmark className="flex-1" /> : null}
          {/* Ein und derselbe Knopf (Fokus bleibt beim Umschalten). */}
          <Tooltip label={expanded ? 'Leiste einklappen' : 'Leiste aufklappen'}>
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={navId}
              aria-label={expanded ? 'Leiste einklappen' : 'Leiste aufklappen'}
              onClick={tablet ? () => setOverlay((v) => !v) : toggle}
              className="grid size-8 flex-none cursor-pointer place-items-center rounded-sidebar text-ink-3 transition-colors duration-150 hover:bg-hover hover:text-ink pointer-coarse:size-11"
            >
              {expanded ? <PanelLeftClose size={18} strokeWidth={1.7} aria-hidden /> : <PanelLeftOpen size={18} strokeWidth={1.7} aria-hidden />}
            </button>
          </Tooltip>
        </div>
        {expanded ? <ExpandedNav id={navId} onNavigate={closeOverlay} userId={me.id} /> : <RailNav id={navId} />}
        <div className={cn('mt-2 flex flex-none flex-col gap-1 pt-2 pointer-coarse:gap-2', expanded ? 'w-full' : 'w-full items-center')}>
          {foot.length > 0 ? (
            <nav aria-label="Verwaltung" className={cn('flex flex-col pointer-coarse:gap-2', expanded ? 'gap-px' : 'items-center gap-1')}>
              {foot.map((item) =>
                expanded ? <WideLink key={item.path} item={item} onNavigate={closeOverlay} /> : <RailLink key={item.path} item={item} />,
              )}
            </nav>
          ) : null}
          <ProfileEntry expanded={expanded} onNavigate={closeOverlay} />
        </div>
      </aside>
    </>
  );
}

/** Zuletzt geöffnete Datensätze (nur breite Leiste; Browser merkt nur Gruppe und Kennung, Namen vom Server – Story 26.9). */
function RecentList({ userId, onNavigate }: { userId: string; onNavigate?: () => void }) {
  const recent = useRecent(userId);
  if (recent.length === 0) return null;
  return (
    <section aria-label="Zuletzt geöffnet" className="mt-5 flex flex-col gap-px">
      <p aria-hidden className="px-[10px] pb-1 text-caption font-medium text-ink-3">
        Zuletzt geöffnet
      </p>
      {recent.map((entry) => (
        <Link
          key={entry.path}
          to={entry.path}
          onClick={onNavigate}
          className="flex h-8 min-w-0 items-center rounded-sidebar px-[10px] text-row text-ink-2 transition-colors duration-150 hover:bg-hover hover:text-ink data-[status=active]:bg-hover data-[status=active]:text-ink pointer-coarse:h-11"
        >
          <span className="min-w-0 truncate">{entry.title}</span>
        </Link>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Schmal: Symbolleiste

/**
 * Runder Symbolknopf mit Beschriftung darunter (Inhalt eines Links oder Knopfs der Symbolleiste). Werte nach Entwurf
 * (Ticket 1.243): Kreis in `fill`, Symbol 19 px mit Strich 1,6, Beschriftung `text-2xs`, Eintrag 72 px breit.
 */
function RailFace({ icon: Icon, label, active, counter }: { icon: NavItem['icon']; label: string; active: boolean; counter?: number }) {
  return (
    <>
      <span
        className={cn(
          // Niedrige Fenster (bis 960 px, z. B. 1440 × 900 mit Probephase-Band): Kreis 36 px, damit alle neun Bereiche samt
          // Beschriftung ohne Scrollen passen – vorher war „Zeiterfassung“ unten abgeschnitten (nur das Uhr-Symbol sichtbar).
          'relative grid size-10 place-items-center rounded-sidebar transition-colors duration-150 [@media(max-height:960px)]:size-9',
          active ? 'bg-hover text-ink' : 'text-ink-2 group-hover:bg-hover group-hover:text-ink',
        )}
      >
        <Icon size={19} strokeWidth={1.7} aria-hidden />
        {counter ? <CounterBadge count={counter} corner /> : null}
      </span>
      <span className={cn('max-w-18 truncate text-2xs leading-rail tracking-rail', active ? 'font-medium text-ink' : 'text-ink-3')}>
        {label}
      </span>
    </>
  );
}

const railItemClass = 'group flex w-16 shrink-0 flex-col items-center gap-[2px] rounded-sidebar py-1 text-center [@media(max-height:960px)]:py-0.5';

/** Bereich mit genau einem Eintrag: direkt der Link. */
function RailLink({ item, counter }: { item: NavItem; counter?: number }) {
  const pathname = usePathname();
  const active = pathMatches(pathname, item.path);
  return (
    <Link to={item.path} activeOptions={{ exact: item.path === '/' }} aria-label={navName(item, counter)} className={railItemClass}>
      <RailFace icon={item.icon} label={item.label} active={active} counter={counter} />
    </Link>
  );
}

function RailNav({ id }: { id: string }) {
  const counterOf = useCounterOf();
  const { areas } = useRoleNav();
  const [openArea, setOpenArea] = useState<string | null>(null);
  return (
    <nav
      id={id}
      aria-label="Hauptnavigation"
      // Eigener Scrollbereich: in niedrigen Fenstern scrollt nur die Navigation, Kopf und Fuß der Leiste bleiben stehen.
      // Beim Scrollen schließt ein offenes Ausklapp-Menü (es steht fest im Fenster neben dem Knopf).
      onScroll={() => setOpenArea(null)}
      className="flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto overscroll-contain py-1 pointer-coarse:gap-2"
    >
      {areas.map((area) =>
        area.items.length === 1 ? (
          <RailLink key={area.label} item={area.items[0]!} counter={counterOf(area.items[0]!)} />
        ) : (
          <RailArea
            key={area.label}
            area={area}
            open={openArea === area.label}
            onOpenChange={(next) => setOpenArea((cur) => (next ? area.label : cur === area.label ? null : cur))}
          />
        ),
      )}
    </nav>
  );
}

/**
 * Bereich mit Ausklapp-Menü (DESIGN S3 `rail-flyout`): Überfahren oder Fokus öffnet, Verlassen schließt (kurz verzögert,
 * damit die Maus vom Knopf ins Menü wandern kann). Tastatur: Tab geht vom Knopf in die Einträge des Menüs, Pfeil runter
 * oder rechts springt hinein, Pfeil hoch/runter wählt, Pfeil links zurück zum Knopf, Esc schließt und gibt den Fokus an
 * den Knopf zurück. Das Menü steht im Quelltext direkt hinter dem Knopf (Tab-Reihenfolge), liegt aber fest im Fenster
 * neben der Leiste (sonst schnitte die scrollende Navigation es ab).
 */
function RailArea({ area, open, onOpenChange }: { area: NavArea; open: boolean; onOpenChange: (open: boolean) => void }) {
  const pathname = usePathname();
  const active = areaActive(area, pathname);
  const menuId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const suppressFocusOpen = useRef(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const show = useCallback(() => {
    cancelClose();
    const button = trigger.current?.getBoundingClientRect();
    const rail = wrapper.current?.closest('aside')?.getBoundingClientRect();
    if (button && rail) setPos({ top: Math.max(8, button.top - 6), left: rail.right + 8 });
    onOpenChange(true);
  }, [onOpenChange]);
  const hide = useCallback(() => {
    cancelClose();
    onOpenChange(false);
  }, [onOpenChange]);
  useEffect(() => cancelClose, []);

  // Menü nicht unter den Fensterrand schieben (z. B. „Versand“ weit unten in niedrigen Fenstern).
  useEffect(() => {
    if (!open || !pos || !menu.current) return;
    const rect = menu.current.getBoundingClientRect();
    const overflow = rect.bottom - (window.innerHeight - 8);
    if (overflow > 0) setPos({ ...pos, top: Math.max(8, pos.top - overflow) });
  }, [open, pos]);

  const links = () => [...(menu.current?.querySelectorAll<HTMLElement>('a[href]') ?? [])];

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const items = links();
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape' && open) {
      // Esc gehört dem Menü (vor den Ebenen am Dokument, siehe modal-focus.ts).
      e.preventDefault();
      hide();
      suppressFocusOpen.current = true;
      trigger.current?.focus();
      suppressFocusOpen.current = false;
      return;
    }
    if (document.activeElement === trigger.current && (e.key === 'ArrowDown' || e.key === 'ArrowRight')) {
      e.preventDefault();
      if (!open) show();
      requestAnimationFrame(() => links()[0]?.focus());
      return;
    }
    if (index < 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = (index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      items[e.key === 'Home' ? 0 : items.length - 1]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      trigger.current?.focus();
    }
  }

  return (
    <div
      ref={wrapper}
      className="flex w-full flex-col items-center"
      onMouseEnter={show}
      onMouseLeave={() => {
        cancelClose();
        closeTimer.current = window.setTimeout(hide, 160);
      }}
      onFocus={() => {
        if (!suppressFocusOpen.current && !open) show();
      }}
      onBlur={(e) => {
        if (!wrapper.current?.contains(e.relatedTarget as Node | null)) hide();
      }}
      onKeyDown={onKeyDown}
    >
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        data-active={active ? 'true' : undefined}
        onClick={() => (open ? undefined : show())}
        className={cn(railItemClass, 'cursor-pointer')}
      >
        <RailFace icon={area.icon} label={area.label} active={active} />
      </button>
      {open && pos ? (
        <div
          ref={menu}
          id={menuId}
          data-testid="ausklapp-menue"
          data-raise
          className="fixed z-40 w-[240px] animate-pop-in rounded-flyout border border-line bg-raised p-[6px] shadow-flyout motion-reduce:animate-none"
          style={{ top: pos.top, left: pos.left }}
        >
          <div aria-hidden className="relative px-[10px] pt-[6px] pb-[6px] text-caption font-medium text-ink-3">
            {area.label}
          </div>
          <ul aria-label={area.label} className="relative flex list-none flex-col gap-px p-0">
            {area.items.map((item) => (
              <li key={item.path}>
                <FlyoutLink item={item} onNavigate={hide} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function FlyoutLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className="group flex h-9 items-center gap-[10px] rounded-sidebar px-[10px] text-row text-ink transition-colors duration-150 hover:bg-hover focus-visible:bg-hover data-[status=active]:bg-hover data-[status=active]:font-medium pointer-coarse:h-11"
    >
      <Icon size={17} strokeWidth={1.7} aria-hidden className="flex-none text-ink-2 group-data-[status=active]:text-link" />
      {item.label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Breit: Symbole + Namen + Zähler, Bereiche aufklappbar

/** Zeile der breiten Leiste und der Schublade: rundes Symbol + Name (+ Zähler); aktiver Punkt als dunkelblaue Pille. */
export function WideLink({ item, counter, onNavigate, large, nested }: { item: NavItem; counter?: number; onNavigate?: () => void; large?: boolean; nested?: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      activeOptions={{ exact: item.path === '/' }}
      onClick={onNavigate}
      className={cn(
        'group flex shrink-0 items-center gap-[10px] rounded-sidebar pr-[10px] text-row whitespace-nowrap text-ink transition-colors duration-150 hover:bg-hover data-[status=active]:bg-hover data-[status=active]:font-medium',
        large ? 'h-11' : 'h-9 pointer-coarse:h-11',
        nested ? 'pl-[38px] text-ink-2 hover:text-ink data-[status=active]:text-ink' : 'pl-[10px]',
      )}
    >
      {nested ? null : (
        <Icon size={18} strokeWidth={1.7} aria-hidden className="flex-none text-ink-2 group-data-[status=active]:text-link" />
      )}
      <span className="min-w-0 truncate">{item.label}</span>
      {counter ? <CounterBadge count={counter} /> : null}
    </Link>
  );
}

/** Bereich der breiten Leiste mit Unterpunkten: Kopfzeile als Knopf (aufklappbar), Einträge eingerückt; anfangs offen. */
export function WideArea({ area, children }: { area: NavArea; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  const listId = useId();
  const active = areaActive(area, usePathname());
  const Icon = area.icon;
  return (
    <div className="flex flex-col gap-px pointer-coarse:gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group flex h-9 cursor-pointer items-center gap-[10px] rounded-sidebar pr-2 pl-[10px] text-row text-ink transition-colors duration-150 hover:bg-hover pointer-coarse:h-11',
          active ? 'font-medium' : '',
        )}
      >
        <Icon size={18} strokeWidth={1.7} aria-hidden className={cn('flex-none', active ? 'text-link' : 'text-ink-2')} />
        {area.label}
        <ChevronDown size={15} strokeWidth={1.7} aria-hidden className={cn('ml-auto text-ink-3 opacity-0 transition duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none', open ? '' : '-rotate-90 opacity-100')} />
      </button>
      <div id={listId} hidden={!open} className="flex flex-col gap-px pointer-coarse:gap-2">
        {children}
      </div>
    </div>
  );
}

function ExpandedNav({ id, onNavigate, userId }: { id: string; onNavigate?: () => void; userId: string }) {
  const counterOf = useCounterOf();
  const { areas } = useRoleNav();
  return (
    // Eigener Scrollbereich; Innenrand für den Fokusring, der sonst am Rand abgeschnitten würde.
    <div className="-mx-1 flex min-h-0 w-[calc(100%+8px)] flex-1 flex-col overflow-y-auto overscroll-contain px-1 py-1">
      <nav id={id} aria-label="Hauptnavigation" className="flex flex-col gap-px pointer-coarse:gap-2">
        {areas.map((area) =>
          area.items.length === 1 ? (
            <WideLink key={area.label} item={area.items[0]!} counter={counterOf(area.items[0]!)} onNavigate={onNavigate} />
          ) : (
            <WideArea key={area.label} area={area}>
              {area.items.map((item) => (
                <WideLink key={item.path} item={item} counter={counterOf(item)} onNavigate={onNavigate} nested />
              ))}
            </WideArea>
          ),
        )}
      </nav>
      <RecentList userId={userId} onNavigate={onNavigate} />
    </div>
  );
}

/**
 * Profil unten: der Profil-Kreis öffnet das Profil-Menü (Story 1.78: Profil öffnen, Hell/Dunkel, Abmelden); breit
 * daneben Name und Firma (Link zum Profil) und „Abmelden“.
 */
function ProfileEntry({ expanded, onNavigate }: { expanded: boolean; onNavigate?: () => void }) {
  const { me } = useSession();
  return (
    <div className={cn('flex items-center gap-1 pointer-coarse:gap-2', expanded ? 'border-t border-line pt-2' : 'justify-center')} aria-label="Profil" role="group">
      <ProfileMenu onNavigate={onNavigate}>
        <Avatar initials={me.initials} />
      </ProfileMenu>
      {expanded ? (
        <>
          <Link
            to="/profil"
            title="Profil öffnen"
            onClick={onNavigate}
            className="flex min-w-0 flex-1 items-center rounded-sidebar px-[6px] py-1 transition-colors duration-150 hover:bg-hover data-[status=active]:bg-hover"
          >
            <div className="min-w-0">
              <b className="block truncate text-row font-medium">{me.displayName}</b>
              <span className="block truncate text-caption text-ink-3">{me.company}</span>
            </div>
          </Link>
          <LogoutButton title="Abmelden" className="size-8 pointer-coarse:size-11" />
        </>
      ) : null}
    </div>
  );
}

/** Abmelden (nur das Symbol); ungespeicherte Änderungen im offenen Datensatz halten erst an (EXPERIENCE §4.3). */
function LogoutButton({ className, title }: { className: string; title?: string }) {
  const { auth } = useSession();
  return (
    <button
      type="button"
      onClick={() => attemptLeave(() => void auth.signOut())}
      aria-label="Abmelden"
      title={title}
      className={cn('grid flex-none cursor-pointer place-items-center rounded-sidebar text-ink-3 transition-colors duration-150 hover:bg-hover hover:text-ink', className)}
    >
      <LogOut size={16} strokeWidth={1.5} aria-hidden />
    </button>
  );
}
